/**
 * Object Data Lookup Utilities
 * Provides efficient lookup functions for object data from localStorage
 * Eliminates duplicate Map creation and find operations
 */

/**
 * Creates a Map of object IDs to names for efficient lookup
 * @param {Array} objectData - Array of object data
 * @returns {Map} Map with idObject as key and name as value
 */
export const createObjectNameMap = (objectData) => {
  const map = new Map();
  if (Array.isArray(objectData)) {
    objectData.forEach((obj) => {
      if (obj && obj.idObject && obj.name) {
        map.set(obj.idObject.toString(), obj.name);
      }
    });
  }
  return map;
};

/**
 * Creates a Map of object IDs to descriptions for efficient lookup
 * @param {Array} objectData - Array of object data
 * @returns {Map} Map with idObject as key and description as value
 */
export const createObjectDescMap = (objectData) => {
  const map = new Map();
  if (Array.isArray(objectData)) {
    objectData.forEach((obj) => {
      if (obj && obj.idObject != null) {
        const key = obj.idObject.toString();
        const descVal = obj.desc ?? obj.description ?? '';
        map.set(key, descVal);
      }
    });
  }
  return map;
};

/**
 * Creates a Map of object codes to object data for efficient lookup
 * @param {Array} objectData - Array of object data
 * @returns {Map} Map with code as key and full object as value
 */
export const createObjectCodeMap = (objectData) => {
  const map = new Map();
  if (Array.isArray(objectData)) {
    objectData.forEach((obj) => {
      if (obj && obj.code != null) {
        map.set(obj.code.toString(), obj);
      }
    });
  }
  return map;
};

/**
 * Finds an object by its ID
 * @param {Array} objectData - Array of object data
 * @param {number|string} idObject - Object ID to find
 * @returns {Object|undefined} Found object or undefined
 */
export const findObjectById = (objectData, idObject) => {
  if (!Array.isArray(objectData) || idObject == null) return undefined;
  return objectData.find((obj) => obj.idObject === parseInt(idObject));
};

/**
 * Finds an object by its code
 * @param {Array} objectData - Array of object data
 * @param {number|string} code - Object code to find
 * @returns {Object|undefined} Found object or undefined
 */
export const findObjectByCode = (objectData, code) => {
  if (!Array.isArray(objectData) || code == null) return undefined;
  return objectData.find((obj) => obj.code === parseInt(code));
};

/**
 * Gets object name by ID
 * @param {Array} objectData - Array of object data
 * @param {number|string} idObject - Object ID
 * @param {string} fallback - Fallback value if not found (default: '')
 * @returns {string} Object name or fallback
 */
export const getObjectName = (objectData, idObject, fallback = '') => {
  const obj = findObjectById(objectData, idObject);
  return obj?.name || fallback;
};

/**
 * Gets object name by code
 * @param {Array} objectData - Array of object data
 * @param {number|string} code - Object code
 * @param {string} fallback - Fallback value if not found (default: field name)
 * @returns {string} Object name or fallback
 */
export const getObjectNameByCode = (objectData, code, fallback = null) => {
  const obj = findObjectByCode(objectData, code);
  return obj?.name || fallback || `Field ${code}`;
};

/**
 * Gets object description by ID
 * @param {Array} objectData - Array of object data
 * @param {number|string} idObject - Object ID
 * @param {string} fallback - Fallback value if not found (default: '')
 * @returns {string} Object description or fallback
 */
export const getObjectDesc = (objectData, idObject, fallback = '') => {
  const obj = findObjectById(objectData, idObject);
  return obj?.desc || obj?.description || fallback;
};

/**
 * Batch lookup - gets multiple object names by IDs
 * More efficient than calling getObjectName multiple times
 * @param {Array} objectData - Array of object data
 * @param {Array} idObjects - Array of object IDs
 * @returns {Map} Map with ID as key and name as value
 */
export const batchGetObjectNames = (objectData, idObjects) => {
  const nameMap = createObjectNameMap(objectData);
  const result = new Map();

  idObjects.forEach((id) => {
    const name = nameMap.get(id.toString());
    if (name) {
      result.set(id, name);
    }
  });

  return result;
};

/**
 * Creates object lookup maps for efficient access
 * Returns all common maps in one go
 * @param {Array} objectData - Array of object data
 * @returns {Object} Object containing name, desc, and code maps
 */
export const createObjectMaps = (objectData) => {
  return {
    nameMap: createObjectNameMap(objectData),
    descMap: createObjectDescMap(objectData),
    codeMap: createObjectCodeMap(objectData),
  };
};
